import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { BehaviorSubject, fromEvent, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ResponseDto } from '../../../dto';
import { LoadingService } from '../../../services';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { AsyncPipe, NgForOf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton } from '@angular/material/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

export interface SelectSearchParamsInterface {
  page: number;
  name?: string;
  pageSize: number;
}

export interface StoreInterface<T> {
  getData: (params: SelectSearchParamsInterface) => Observable<ResponseDto<T>>
}

const LOADNIG_TAG: string = 'app-select-load-more-loading-tag';

@UntilDestroy()
@Component({
  selector: 'app-select-load-more',
  templateUrl: './select-load-more.component.html',
  styleUrls: ['./select-load-more.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectLoadMoreComponent),
      multi: true,
    },
  ],
  imports: [
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatProgressSpinner,
    NgForOf,
    AsyncPipe,
    MatIconButton,
    MatSuffix,
    FontAwesomeModule,
  ],
})
export class SelectLoadMoreComponent<T, R> implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() labelKey: string;
  @Input() valueKey: string;
  @Input() set store(value: StoreInterface<T>) {
    if (value) {
      this.innerStoreSubject.next(value);
      if (this.currentValue && !this.currentItem) {
        this.loadPreselectedItem(this.currentValue);
      } else {
        this.onSearchChanged();
      }
    }
  }
  get store() {
    return this.innerStoreSubject.getValue();
  }

  @Input() label: string;
  @Input() placeholder: string;
  @Input() showClear: boolean;
  @Input() emptyOptionLabel: string = 'No options';

  @Output() valueChangeEvent: EventEmitter<T | null> = new EventEmitter<T | null>();
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChildren(MatOption) matOptions!: QueryList<MatOption>;
  readonly isLoading$: Observable<boolean>;

  options: T[] = [];
  isLoading: boolean = false;
  page: number = 0;
  pageSize: number = 20;
  totalCount: number;
  resultCount: number;

  readonly ctrl: FormControl = new FormControl();

  private readonly innerStoreSubject: BehaviorSubject<StoreInterface<T>> = new BehaviorSubject(null);
  private readonly searchSubject: BehaviorSubject<symbol> = new BehaviorSubject(null);
  private currentValue: R = null;
  private currentItem: T = null;
  private loadMoreSubscription: Subscription | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
  ) {
    this.isLoading$ = this.loadingService.getIsLoading(LOADNIG_TAG);
  }

  writeValue(value: R) {
    if (!value) {
      if (this.matOptions) {
        this.matOptions.forEach(option => {
          if (option.selected) {
            option.deselect();
          }
        });
      }

      this.currentValue = null;
      this.currentItem = null;
      this.ctrl.setValue(null, { emitEvent: false });
      this.cdr.detectChanges();
      return;
    }

    let normalizedValue: R = value;
    if (this.valueKey && typeof value === 'object' && value !== null) {
      // @ts-ignore
      if (value[this.valueKey] !== undefined) {
        // @ts-ignore
        normalizedValue = value[this.valueKey];
      }
    }

    this.currentValue = normalizedValue;

    const foundItem = this.findItemByValue(normalizedValue);
    if (foundItem) {
      this.currentItem = foundItem;
      const formValue: R = this.valueKey ? foundItem[this.valueKey] : (foundItem as any);
      this.currentValue = formValue;
      this.ctrl.setValue(formValue, { emitEvent: false });
      this.selectMatOption(formValue);
      this.cdr.detectChanges();
      return;
    }

    if (this.store && this.valueKey) {
      this.loadPreselectedItem(normalizedValue);
    } else {
      this.ctrl.setValue(normalizedValue, { emitEvent: false });
      this.cdr.detectChanges();
    }
  }

  private findItemByValue(value: R): T | null {
    if (!value) return null;

    if (this.valueKey) {
      let found = this.options.find((option: T) => option[this.valueKey] === value);
      if (found) return found;

      if (typeof value === 'object' && value !== null) {
        // @ts-ignore
        found = this.options.find((option: T) => option === value);
        if (found) return found;

        if (value[this.valueKey] !== undefined) {
          // @ts-ignore
          found = this.options.find((option: T) => option[this.valueKey] === value[this.valueKey]);
          if (found) return found;
        }
      }
      return null;
    } else {
      // @ts-ignore
      return this.options.find((option: T) => option === value) || null;
    }
  }

  private loadPreselectedItem(value: R): void {
    if (!this.store || !this.valueKey || this.isLoading) return;

    this.isLoading = true;
    this.page = 1;
    const baseParams: SelectSearchParamsInterface = {
      page: 1,
      pageSize: this.pageSize,
    };
    const preselectedParams: SelectSearchParamsInterface = {
      page: 1,
      pageSize: 1,
      name: String(value),
    };

    const preselected$ = this.getData(preselectedParams).pipe(
      map((res: ResponseDto<T>) => this.findItemInResponse(value, res)),
      catchError(() => of(null)),
    );

    this.getData(baseParams).pipe(
      map((res: ResponseDto<T>) => {
        this.options = res.content;
        this.totalCount = res.totalCount;
        this.resultCount = res.resultCount;

        return this.findItemInResponse(value, res);
      }),
      switchMap((foundItem: T | null) => {
        if (foundItem) {
          return of({ item: foundItem, prepend: false, baseLoaded: true });
        }

        return preselected$.pipe(
          map((item: T | null) => ({ item, prepend: true, baseLoaded: true }))
        );
      }),
      catchError(() => preselected$.pipe(
        map((item: T | null) => ({ item, prepend: false, baseLoaded: false }))
      )),
      finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }),
      take(1),
      untilDestroyed(this),
    ).subscribe(({ item, prepend, baseLoaded }) => {
      if (item) {
        this.currentItem = item;
        if (prepend && !this.findItemByValue(value)) {
          this.options = [item, ...this.options];
        } else if (!baseLoaded) {
          this.options = [item];
        }
      }

      this.ctrl.setValue(value, { emitEvent: false });
      this.selectMatOption(value);
    });
  }

  onChange = (value: R) => {};

  onTouched = () => {};

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  onSearchChanged(): void {
    const inputValue = this.ctrl.value;

    if (this.currentValue && this.currentItem) {
      const currentLabel = this.currentItem[this.labelKey] || '';
      if (typeof inputValue === 'string' && inputValue !== currentLabel) {
        this.currentValue = null;
        this.currentItem = null;
        this.onChange(null);
        this.searchSubject.next(Symbol(''));
      }
    } else {
      this.searchSubject.next(Symbol(''));
    }
  }

  onBlur(): void {
    if (this.currentValue && this.currentItem) {
      const currentLabel = this.currentItem[this.labelKey] || '';
      const inputValue = this.ctrl.value;

      if (typeof inputValue === 'string' && inputValue !== currentLabel) {
        this.ctrl.setValue(this.currentValue, { emitEvent: false });
      }
    }
    this.onTouched();
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.ctrl.disable({ emitEvent: false });
    } else {
      this.ctrl.enable({ emitEvent: false });
    }
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      untilDestroyed(this),
    ).subscribe(() => {
      this.loadMore(true);
    });
  }

  onOptionSelected(value: R): void {
    const foundItem = this.findItemByValue(value);
    if (foundItem) {
      const formValue: R = this.valueKey ? foundItem[this.valueKey] : (foundItem as any);

      this.currentItem = foundItem;
      this.currentValue = formValue;

      this.ctrl.setValue(formValue, { emitEvent: false });

      this.onChange(formValue);

      this.valueChangeEvent.emit(foundItem);
      this.onTouched();
    } else {
      this.currentItem = null;
      this.currentValue = null;
      this.ctrl.setValue(null, { emitEvent: false });
      this.onChange(null);
      this.valueChangeEvent.emit(null);
      this.onTouched();
    }
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.matAutocomplete.opened.pipe(
      debounceTime(50),
      untilDestroyed(this)
    ).subscribe(() => {
      const panel = this.matAutocomplete.panel?.nativeElement;

      if (!panel) return;

      fromEvent(panel, 'scroll')
      .pipe(
        map(() => panel.scrollTop + panel.clientHeight >= panel.scrollHeight),
        filter(atBottom => atBottom)
      )
      .subscribe(() => {
        this.loadMore();
      });
    });
  }

  loadMore(isNewSearch?: boolean): void {
    if (!this.store) return;
    if (!isNewSearch && this.totalCount && this.resultCount && this.totalCount === this.resultCount) return;
    if (isNewSearch) {
      this.page = 0;
      this.totalCount = null;
      this.resultCount = null;
    }

    if (this.loadMoreSubscription) {
      this.loadMoreSubscription.unsubscribe();
      this.loadMoreSubscription = null;
    }

    this.isLoading = true;
    const requestPage: number = isNewSearch ? 1 : this.page + 1;

    const inputValue = this.ctrl.value;
    let searchTerm = '';

    if (typeof inputValue === 'string') {
      const currentLabel = this.currentItem?.[this.labelKey];
      if (inputValue !== currentLabel) {
        searchTerm = inputValue;
      }
    }

    this.loadMoreSubscription = this.getData({
      page: requestPage,
      pageSize: this.pageSize,
      ...(searchTerm?.length ? { name: searchTerm } : {})
    }).pipe(
      take(1),
      untilDestroyed(this),
      finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }),
    ).subscribe((res: ResponseDto<T>) => {
      if (isNewSearch) {
        this.options = [];
      }
      this.page = requestPage;
      this.options = [...this.options, ...res.content];
      this.totalCount = res.totalCount;
      this.resultCount = res.resultCount;

      if (this.currentValue && !this.currentItem) {
        const foundItem = this.findItemByValue(this.currentValue);
        if (foundItem) {
          this.currentItem = foundItem;
          this.ctrl.setValue(this.currentValue, { emitEvent: false });
        }
      }

    });
  }

  private findItemInResponse(value: R, res: ResponseDto<T>): T | null {
    if (!res?.content?.length) return null;

    return res.content.find((item: T) => {
      if (this.valueKey) {
        return item[this.valueKey] === value;
      }
      // @ts-ignore
      return item === value;
    }) || null;
  }

  displayFn(value: R): string {
    if (!value) return '';

    let item: T = null;

    if (this.currentItem) {
      if (this.valueKey) {
        if (this.currentItem[this.valueKey] === value) {
          item = this.currentItem;
        }
      } else {
        // @ts-ignore
        if (this.currentItem === value) {
          item = this.currentItem;
        }
      }
    }

    if (!item) {
      if (this.valueKey) {
        item = this.options?.find((option: T) => option[this.valueKey] === value) || null;
      } else {
        // @ts-ignore
        item = this.options?.find((option: T) => option === value) || null;
      }
    }

    if (!item && this.currentItem && this.currentValue === value) {
      item = this.currentItem;
    }

    return item?.[this.labelKey] || '';
  }

  private selectMatOption(value: R): void {
    if (!this.matOptions || !value) return;

    setTimeout(() => {
      if (this.matOptions) {
        this.matOptions.forEach(option => {
          const optionValue = option.value;
          if (optionValue === value) {
            if (!option.selected) {
              option.select();
            }
          } else {
            if (option.selected) {
              option.deselect();
            }
          }
        });
      }
    }, 0);
  }

  clearAll(): void {
    if (this.matOptions) {
      this.matOptions.forEach(option => {
        if (option.selected) {
          option.deselect();
        }
      });
    }

    this.currentValue = null;
    this.currentItem = null;

    this.ctrl.setValue(null, { emitEvent: false });

    this.page = 0;
    this.options = [];
    this.totalCount = null;
    this.resultCount = null;
    this.loadMore(true);

    this.onChange(null);
    this.valueChangeEvent.emit(null);
    this.onTouched();

    this.cdr.detectChanges();
  }

  private getData(params: SelectSearchParamsInterface): Observable<ResponseDto<T>> {
    return this.loadingService.loadingWrapper(this.store.getData(params), LOADNIG_TAG);
  }
}
